import { useRef, useEffect, useState } from 'react';

const qualities = [
  { icon: 'ri-user-star-line', title: 'Skilled Installation Team', desc: 'Trained specialists who treat every floor like it\'s their own.' },
  { icon: 'ri-focus-3-line', title: 'Precision & Attention to Detail', desc: 'Every seam, edge, and transition finished to perfection.' },
  { icon: 'ri-time-line', title: 'Reliable Timelines', desc: 'We show up on time, work efficiently, and deliver on schedule.' },
  { icon: 'ri-home-heart-line', title: 'Clean & Professional', desc: 'Minimal disruption to your home — we clean up after ourselves.' },
  { icon: 'ri-trophy-line', title: 'Proven Local Reputation', desc: 'Trusted by homeowners and businesses across New Jersey for years.' },
  { icon: 'ri-leaf-line', title: 'Quality Materials Only', desc: 'We work with premium-grade flooring for lasting performance.' },
];

export default function WhyChooseUs() {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} id="about" className="py-24 lg:py-32" style={{ background: '#FFFFFF' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left — Image */}
          <div
            className="relative"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateX(0)' : 'translateX(-32px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease',
            }}
          >
            <div className="relative rounded-lg overflow-hidden" style={{ height: '600px' }}>
              <img
                src="https://public.readdy.ai/ai/img_res/15a3cfa1a34600b3f658ffc63140098c.jpg"
                alt="Expert flooring installation craftsmanship"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 to-transparent" />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-lg p-6" style={{ boxShadow: '0 16px 48px rgba(44,44,44,0.15)' }}>
              <div className="flex items-center gap-3 mb-2">
                {[1,2,3,4,5].map(s => (
                  <span key={s} className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-gold text-sm"></i>
                  </span>
                ))}
              </div>
              <p className="font-serif text-charcoal text-base">5-Star Rated Service</p>
              <p className="text-warm-gray text-xs mt-0.5">Freehold, NJ &amp; Surrounding Areas</p>
            </div>
          </div>

          {/* Right — Content */}
          <div
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateX(0)' : 'translateX(32px)',
              transition: 'opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s',
            }}
          >
            <span className="section-tag mb-4 block">Why Choose Value Carpet</span>
            <h2 className="font-serif text-4xl lg:text-5xl text-charcoal leading-tight mb-6">
              Built for Quality.<br />
              <em className="text-gold not-italic">Designed to Last.</em>
            </h2>
            <p className="text-warm-gray text-base leading-relaxed mb-12">
              Every project we take on reflects our commitment to quality workmanship, honest service, and lasting results. That reputation is everything to us.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {qualities.map((q, i) => (
                <div
                  key={q.title}
                  className="flex items-start gap-4"
                  style={{
                    opacity: inView ? 1 : 0,
                    transform: inView ? 'translateY(0)' : 'translateY(16px)',
                    transition: `opacity 0.5s ease ${0.3 + i * 0.07}s, transform 0.5s ease ${0.3 + i * 0.07}s`,
                  }}
                >
                  <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-md bg-cream">
                    <i className={`${q.icon} text-gold text-base`}></i>
                  </span>
                  <div>
                    <p className="font-semibold text-charcoal text-sm mb-1">{q.title}</p>
                    <p className="text-warm-gray text-xs leading-relaxed">{q.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
